import React, { useState } from 'react';
import { Calendar, Image, Video, Link as LinkIcon, Send, Instagram, Facebook, Youtube, MessageSquare } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import SocialMediaPostModal from '../../components/admin/SocialMediaPostModal';
import SocialMediaCalendar from '../../components/admin/SocialMediaCalendar';
import SocialMediaAnalytics from '../../components/admin/SocialMediaAnalytics';

export default function SocialMediaPage() {
  const [showPostModal, setShowPostModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'posts' | 'calendar' | 'analytics'>('posts');

  const mockPosts = [
    {
      id: '1',
      content: 'Découvrez notre nouvelle destination : Les Maldives ! 🌊✨',
      media: ['https://images.unsplash.com/photo-1514282401047-d79a71a590e8'],
      platforms: ['instagram', 'facebook'],
      status: 'scheduled',
      scheduledFor: '2024-03-15T10:00:00Z',
    },
    {
      id: '2',
      content: 'Guide complet pour visiter Dubai en famille 🌆👨‍👩‍👧‍👦',
      media: ['https://images.unsplash.com/photo-1512453979798-5ea266f8880c'],
      platforms: ['instagram', 'facebook', 'youtube'],
      status: 'draft',
    },
  ];

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Réseaux Sociaux</h1>
          <button
            onClick={() => setShowPostModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Nouvelle publication
          </button>
        </div>

        {/* Navigation des onglets */}
        <div className="bg-white shadow rounded-lg">
          <nav className="flex space-x-8 px-6 border-b">
            <button
              onClick={() => setActiveTab('posts')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'posts'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Publications
            </button>
            <button
              onClick={() => setActiveTab('calendar')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'calendar'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Calendrier
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'analytics'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Analyses
            </button>
          </nav>

          {/* Contenu principal */}
          <div className="p-6">
            {activeTab === 'posts' && (
              <div className="space-y-6">
                {/* Statistiques rapides */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                      <Instagram className="w-8 h-8 text-pink-600" />
                      <div className="ml-4">
                        <p className="text-sm text-gray-500">Instagram</p>
                        <p className="text-2xl font-semibold">2.5K</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                      <Facebook className="w-8 h-8 text-blue-600" />
                      <div className="ml-4">
                        <p className="text-sm text-gray-500">Facebook</p>
                        <p className="text-2xl font-semibold">3.8K</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                      <Youtube className="w-8 h-8 text-red-600" />
                      <div className="ml-4">
                        <p className="text-sm text-gray-500">YouTube</p>
                        <p className="text-2xl font-semibold">1.2K</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                      <MessageSquare className="w-8 h-8 text-blue-400" />
                      <div className="ml-4">
                        <p className="text-sm text-gray-500">Messages</p>
                        <p className="text-2xl font-semibold">28</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Liste des publications */}
                <div className="bg-white shadow-sm rounded-lg border">
                  <div className="p-6">
                    <h2 className="text-lg font-medium mb-6">Publications récentes</h2>
                    <div className="space-y-6">
                      {mockPosts.map((post) => (
                        <div key={post.id} className="border rounded-lg p-4">
                          <div className="flex justify-between items-start mb-4">
                            <div className="flex items-center space-x-2">
                              {post.platforms.includes('instagram') && (
                                <Instagram className="w-5 h-5 text-pink-600" />
                              )}
                              {post.platforms.includes('facebook') && (
                                <Facebook className="w-5 h-5 text-blue-600" />
                              )}
                              {post.platforms.includes('youtube') && (
                                <Youtube className="w-5 h-5 text-red-600" />
                              )}
                            </div>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              post.status === 'scheduled'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {post.status === 'scheduled' ? 'Programmé' : 'Brouillon'}
                            </span>
                          </div>
                          <p className="text-gray-900 mb-4">{post.content}</p>
                          {post.media && post.media.length > 0 && (
                            <div className="grid grid-cols-2 gap-4 mb-4">
                              {post.media.map((url, index) => (
                                <img
                                  key={index}
                                  src={url}
                                  alt=""
                                  className="w-full h-48 object-cover rounded-lg"
                                />
                              ))}
                            </div>
                          )}
                          {post.scheduledFor && (
                            <div className="flex items-center text-sm text-gray-500">
                              <Calendar className="w-4 h-4 mr-2" />
                              Programmé pour le{' '}
                              {new Date(post.scheduledFor).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: 'numeric',
                              })}
                            </div>
                          )}
                          <div className="mt-4 flex justify-end space-x-4">
                            <button className="text-gray-600 hover:text-gray-900">
                              Modifier
                            </button>
                            <button className="text-red-600 hover:text-red-900">
                              Supprimer
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'calendar' && <SocialMediaCalendar />}
            {activeTab === 'analytics' && <SocialMediaAnalytics />}
          </div>
        </div>

        <SocialMediaPostModal
          isOpen={showPostModal}
          onClose={() => setShowPostModal(false)}
        />
      </div>
    </AdminLayout>
  );
}